import React from "react";

import { isNonOAuthIntegration, selectsCrawlerPreferences } from "../constants";

import { useIntegrationActions } from "./useIntegrationActions";
import { useIntegrationURL } from "./useIntegrationURL";

import type { IntegrationScope } from "../constants";
import type { Integration } from "../types/integrations";
import type { ModalStep } from "../types/modal";

interface UseIntegrationModalHandlersProps {
  integration: Integration | null;
  onClose: () => void;
  setStep: (step: ModalStep) => void;
  setCrawlerPreferenceError: (error: string | null) => void;
  setFromCrawlerPreference: (value: boolean) => void;
  setSelectedAccountId: (id: string | undefined) => void;
  setShowNonOAuthForm?: (show: boolean) => void;
  integrationScope?: IntegrationScope;
}

export function useIntegrationModalHandlers({
  integration,
  onClose,
  setStep,
  setCrawlerPreferenceError,
  setFromCrawlerPreference,
  setSelectedAccountId,
  setShowNonOAuthForm,
  integrationScope,
}: UseIntegrationModalHandlersProps) {
  const { connectIntegration } = useIntegrationActions(integrationScope);
  const { clearModalURL } = useIntegrationURL();

  const handleConnect = React.useCallback(
    async (accountId?: string) => {
      if (!integration) return;

      setSelectedAccountId(accountId);

      const isOAuthIntegration = !isNonOAuthIntegration(integration.label);

      if (isOAuthIntegration) {
        if (import.meta.env.DEV) {
          const needsCrawlerPreferences = selectsCrawlerPreferences(
            integration.label,
            integrationScope,
          );
          setFromCrawlerPreference(false);

          if (needsCrawlerPreferences) {
            setStep("crawler_preference");
          } else {
            setStep("result");
          }
        } else {
          connectIntegration(integration.label);
        }
      } else {
        // For non-OAuth integrations, show the authentication form
        setShowNonOAuthForm?.(true);
        setStep("authenticate");
      }
    },
    [
      integration,
      setSelectedAccountId,
      setFromCrawlerPreference,
      setStep,
      setShowNonOAuthForm,
      connectIntegration,
      integrationScope,
    ],
  );

  const handleCrawlerPreferenceNext = React.useCallback(() => {
    setCrawlerPreferenceError(null);
    setStep("result");
  }, [setCrawlerPreferenceError, setStep]);

  const handleCrawlerPreferenceError = React.useCallback(
    (error?: string) => {
      setCrawlerPreferenceError(
        error || "Failed to update crawler preferences",
      );
      setStep("result");
    },
    [setCrawlerPreferenceError, setStep],
  );

  const handleManage = React.useCallback(
    (accountId?: string) => {
      setSelectedAccountId(accountId);
      setFromCrawlerPreference(true);
      setStep("crawler_preference");
    },
    [setSelectedAccountId, setFromCrawlerPreference, setStep],
  );

  const handleSetAccountId = React.useCallback(
    async (accountId: string) => {
      if (!integration) return;

      // Set the account ID as the selectedAccountId (verifiable ID)
      setSelectedAccountId(accountId);

      // For non-OAuth integrations with account IDs, proceed to crawler preferences
      const needsCrawlerPreferences = selectsCrawlerPreferences(
        integration.label,
        integrationScope,
      );
      if (needsCrawlerPreferences) {
        setFromCrawlerPreference(false);
        setStep("crawler_preference");
      } else {
        setStep("result");
      }
    },
    [
      integration,
      setFromCrawlerPreference,
      setStep,
      setSelectedAccountId,
      integrationScope,
    ],
  );

  const handleClose = React.useCallback(() => {
    clearModalURL();
    onClose();
  }, [clearModalURL, onClose]);

  return {
    handleConnect,
    handleCrawlerPreferenceNext,
    handleCrawlerPreferenceError,
    handleManage,
    handleClose,
    handleSetAccountId,
  };
}
